<script>
import _ from 'underscore';
import lodash from 'lodash';
import Vue from 'vue';
import ClyDropdown from '../dropdown/cly-dropdown.vue';
import countlyCommon from '../../compat/countly.common';
import countlyVue from '../../compat/vue-core';
import i18n from '../../../../i18n';

const TableExtensionsMixin = {
  props: {
    persistKey: {
      type: String,
      default: null,
    },
    dataSource: {
      type: Object,
      default() {
        return null;
      },
    },
    rows: {
      type: Array,
      default() {
        return [];
      },
    },
    availableDynamicCols: {
      type: Array,
      default() {
        return [];
      },
    },
    paused: {
      type: Boolean,
      default: false,
    },
    displaySearch: {
      type: Boolean,
      default: true,
    },
    defaultSort: {
      type: Object,
      default() {
        return { prop: '_id', order: 'asc' };
      },
      required: false,
    },
  },
  computed: {
    hasDynamicCols() {
      return this.availableDynamicCols.length > 0;
    },
    availableDynamicColsLookup() {
      return this.availableDynamicCols.reduce((acc, col) => {
        acc[col.value] = col;
        return acc;
      }, {});
    },
    publicDynamicCols() {
      const cols = this.controlParams.selectedDynamicCols.map((val) => this.availableDynamicColsLookup[val]).filter((val) => !!val);
      return cols;
    },
    localSearchedRows() {
      let currentArray = this.rows.slice();
      if (this.displaySearch && this.controlParams.searchQuery) {
        const queryLc = (`${this.controlParams.searchQuery}`).toLowerCase();
        currentArray = currentArray.filter((item) => Object.keys(item).some((fieldKey) => {
          if (item[fieldKey] === null || item[fieldKey] === undefined) {
            return false;
          }
          return (`${item[fieldKey]}`).toLowerCase().indexOf(queryLc) > -1;
        }));
      }
      return currentArray;
    },
    localDataView() {
      let currentArray = this.localSearchedRows;
      if (this.controlParams.sort.length > 0) {
        const sorting = this.controlParams.sort[0];
        const dir = sorting.type === 'asc' ? 1 : -1;

        currentArray = currentArray.slice();
        currentArray.sort((a, b) => {
          let priA = a[sorting.field];
          let priB = b[sorting.field];

          if (typeof priA === 'object' && priA !== null && priA.sortBy) {
            priA = priA.sortBy;
            priB = priB.sortBy;
          }

          if (priA < priB) {
            return -dir;
          }
          if (priA > priB) {
            return dir;
          }
          return 0;
        });
      }
      const filteredTotal = currentArray.length;
      if (this.controlParams.perPage < currentArray.length) {
        const startIndex = (this.controlParams.page - 1) * this.controlParams.perPage;
        const endIndex = startIndex + this.controlParams.perPage;
        currentArray = currentArray.slice(startIndex, endIndex);
      }
      return {
        rows: currentArray,
        totalRows: filteredTotal,
        notFilteredTotalRows: this.rows.length,
      };
    },
    dataView() {
      if (this.dataSource) {
        return this.externalData;
      }

      return this.localDataView;
    },
    totalPages() {
      return Math.ceil(this.dataView.totalRows / this.controlParams.perPage);
    },
    lastPage() {
      return this.totalPages;
    },
    prevAvailable() {
      return this.controlParams.page > this.firstPage;
    },
    nextAvailable() {
      return this.controlParams.page < this.lastPage;
    },
    paginationInfo() {
      const { page } = this.controlParams;
      const { perPage } = this.controlParams;
      const { searchQuery } = this.controlParams;
      const grandTotal = this.dataView.notFilteredTotalRows;
      const filteredTotal = this.dataView.totalRows;
      const startEntries = (page - 1) * perPage + 1;
      const endEntries = Math.min(startEntries + perPage - 1, filteredTotal);
      let info = i18n.tc('common.table.no-data');

      if (filteredTotal > 0) {
        info = i18n.tc('common.showing')
          .replace('_START_', countlyCommon.formatNumber(startEntries))
          .replace('_END_', countlyCommon.formatNumber(endEntries))
          .replace('_TOTAL_', countlyCommon.formatNumber(filteredTotal));
      }

      if (this.displaySearch && searchQuery) {
        info += ` ${i18n.tc('common.filtered').replace('_MAX_', grandTotal)}`;
      }
      return info;
    },
    externalData() {
      if (!this.dataSource) {
        return [];
      }
      return this.dataSource.rows.value;
    },
    externalStatus() {
      if (!this.dataSource) {
        return undefined;
      }
      return this.dataSource.status.value;
    },
    externalParams() {
      if (!this.dataSource) {
        return undefined;
      }
      return this.dataSource.params.value;
    },
    availablePages() {
      const pages = [];
      for (let i = this.firstPage, I = Math.min(this.lastPage, 10000); i <= I; i += 1) {
        pages.push(i);
      }
      return pages;
    },
  },
  watch: {
    controlParams: {
      deep: true,
      handler: _.debounce(function () {
        this.triggerExternalSource();
        this.setControlParams();
      }, 500),
    },
    'controlParams.page': function () {
      this.checkPageBoundaries();
    },
    'controlParams.selectedDynamicCols': function () {
      this.$refs.elTable.store.updateColumns(); // TODO: Hacky, check for memory leaks.
    },
    'controlParams.searchQuery': function (newVal) {
      this.$emit('search-query-changed', newVal);
    },
    lastPage() {
      this.checkPageBoundaries();
    },
    paused(newVal) {
      if (newVal) {
        this.dataSource.setParams({
          ready: false,
        });
      } else {
        this.triggerExternalSource();
      }
    },
  },
  data() {
    const controlParams = this.getControlParams();

    if (!controlParams.selectedDynamicCols || !Array.isArray(controlParams.selectedDynamicCols)) {
      controlParams.selectedDynamicCols = this.availableDynamicCols.reduce((acc, option) => {
        if (option.default || option.required) {
          acc.push(option.value);
        }
        return acc;
      }, []);
    }

    return {
      controlParams,
      firstPage: 1,
    };
  },
  mounted() {
    this.triggerExternalSource();
  },
  beforeDestroy() {
    this.setControlParams();
  },
  methods: {
    checkPageBoundaries() {
      if (this.lastPage > 0 && this.controlParams.page > this.lastPage) {
        this.controlParams.page = this.lastPage;
      }
      if (this.controlParams.page < 1) {
        this.controlParams.page = 1;
      }
    },
    goToFirstPage() {
      this.controlParams.page = this.firstPage;
    },
    goToLastPage() {
      this.controlParams.page = this.lastPage;
    },
    goToPrevPage() {
      if (this.prevAvailable) {
        this.controlParams.page -= 1;
      }
    },
    goToNextPage() {
      if (this.nextAvailable) {
        this.controlParams.page += 1;
      }
    },
    onSortChange(elTableSorting) {
      if (elTableSorting.order) {
        this.updateControlParams({
          sort: [{
            field: elTableSorting.prop,
            type: elTableSorting.order === 'ascending' ? 'asc' : 'desc',
          }],
        });
      } else {
        this.updateControlParams({
          sort: [],
        });
      }
    },
    triggerExternalSource() {
      if (!this.dataSource || this.paused) {
        return;
      }
      if (this.dataSource.fetch) {
        this.dataSource.fetch(this.controlParams);
      }
      this.$emit('params-change', this.controlParams);
    },
    updateControlParams(newParams) {
      _.extend(this.controlParams, newParams);
    },
    getControlParams() {
      const defaultState = {
        page: 1,
        perPage: 10,
        searchQuery: '',
        sort: [],
        selectedDynamicCols: false,
      };

      if (this.defaultSort) {
        defaultState.sort = [{
          field: this.defaultSort.prop,
          type: this.defaultSort.order === 'ascending' ? 'asc' : 'desc',
        }];
      }

      if (!this.persistKey) {
        return defaultState;
      }
      const loadedState = localStorage.getItem(this.persistKey);
      try {
        if (loadedState) {
          const parsed = JSON.parse(loadedState);
          // disable loading of persisted searchQuery
          parsed.searchQuery = ''; // but we still need the field to be present for reactivity
          return parsed;
        }
        return defaultState;
      } catch (ex) {
        return defaultState;
      }
    },
    setControlParams() {
      if (this.persistKey) {
        localStorage.setItem(this.persistKey, JSON.stringify(this.controlParams));
      }
    },
  },
};

const MutationTrackerMixin = {
  data() {
    return {
      patches: {},
      hasSelection: false,
    };
  },
  props: {
    trackedFields: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  watch: {
    sourceRows(newSourceRows) {
      if (Object.keys(this.patches).length === 0) {
        return;
      }

      newSourceRows.forEach((row) => {
        const rowKey = this.keyOf(row);
        if (!this.patches[rowKey]) {
          return;
        }
        const sourceChanges = Object.keys(this.patches[rowKey]).reduce((acc, fieldKey) => {
          const currentPatch = this.patches[rowKey][fieldKey];
          if (currentPatch.originalValue !== row[fieldKey]) {
            acc[fieldKey] = { originalValue: row[fieldKey], newValue: currentPatch.newValue };
          } else if (currentPatch.newValue !== row[fieldKey]) {
            acc[fieldKey] = currentPatch;
          }
          return acc;
        }, {});
        Vue.set(this.patches, rowKey, sourceChanges);
      });
    },
  },
  methods: {
    keyOf(row, dontStringify) {
      if (dontStringify) {
        return this.keyFn(row);
      }
      return JSON.stringify(this.keyFn(row));
    },
    patch(row, fields) {
      const rowKey = this.keyOf(row);
      let newPatch = Object.keys(fields).reduce((acc, fieldKey) => {
        if (this.patches[rowKey] && Object.prototype.hasOwnProperty.call(this.patches[rowKey], fieldKey)) {
          const { newValue } = this.patches[rowKey][fieldKey];
          const { originalValue } = this.patches[rowKey][fieldKey];
          if (newValue !== fields[fieldKey]) {
            acc[fieldKey] = { originalValue, newValue: fields[fieldKey] };
          }
        } else if (row[fieldKey] !== fields[fieldKey]) {
          acc[fieldKey] = { originalValue: row[fieldKey], newValue: fields[fieldKey] };
        }
        return acc;
      }, {});

      newPatch = lodash.merge({}, this.patches[rowKey] || {}, newPatch);

      Vue.set(this.patches, rowKey, newPatch);
    },
    unpatch(row, fields) {
      let rowKeys = null;
      if (!row) {
        rowKeys = Object.keys(this.patches);
      } else {
        rowKeys = [this.keyOf(row)];
      }

      rowKeys.forEach((rowKey) => {
        if (!this.patches[rowKey]) {
          return;
        }

        if (!fields) {
          Vue.delete(this.patches, rowKey);
        } else {
          fields.forEach((fieldName) => {
            Vue.delete(this.patches[rowKey], fieldName);
          });
          if (Object.keys(this.patches[rowKey]).length === 0) {
            Vue.delete(this.patches, rowKey);
          }
        }
      });
    },
  },
  computed: {
    diff() {
      if (this.trackedFields.length === 0 || Object.keys(this.patches).length === 0) {
        return [];
      }
      const diff = [];
      Object.keys(this.patches).forEach((rowKey) => {
        this.trackedFields.forEach((fieldName) => {
          if (this.patches[rowKey] && Object.prototype.hasOwnProperty.call(this.patches[rowKey], fieldName)) {
            const patch = this.patches[rowKey][fieldName];
            if (patch.originalValue !== patch.newValue) {
              diff.push({
                key: JSON.parse(rowKey),
                field: fieldName,
                newValue: patch.newValue,
                originalValue: patch.originalValue,
              });
            }
          }
        });
      });
      return diff;
    },
    mutatedRows() {
      if (Object.keys(this.patches).length === 0) {
        return this.sourceRows;
      }
      return this.sourceRows.map((row) => {
        const rowKey = this.keyOf(row);
        if (this.patches[rowKey]) {
          const newValues = Object.keys(this.patches[rowKey]).reduce((acc, fieldKey) => {
            acc[fieldKey] = this.patches[rowKey][fieldKey].newValue;
            return acc;
          }, {});
          return { ...row, ...newValues };
        }
        return row;
      });
    },
  },
};

const DEFAULT_ROW = {
  rowspan: 1,
  colspan: 1,
};

const NO_COL_ROW = {
  rowspan: 1,
  colspan: 0,
};

const OverlayRowMixin = {
  props: {
    isOverlayActiveFn: {
      type: Function,
      default: null,
    },
  },
  methods: {
    tableSpanMethod(obj) {
      if (this.isOverlayActiveFn && this.isOverlayActiveFn(obj)) {
        if (obj.column.type !== 'overlay') {
          return NO_COL_ROW;
        }

        return {
          rowspan: 1,
          colspan: this.$refs.elTable.columns.length,
        };
      }

      return DEFAULT_ROW;
    },
    onCellMouseEnter(row) {
      if (this.hasSelection) {
        // If the table is in a selection mode, donot patch
        // Because if we patch to show action buttons the selection will be lost
        return;
      }

      const thisRowKey = this.keyOf(row);
      const hovered = this.mutatedRows.filter((r) => r.hover);

      for (let i = 0; i < hovered.length; i += 1) {
        const rowKey = this.keyOf(hovered[i]);

        if (thisRowKey !== rowKey) {
          this.unpatch(hovered[i], ['hover']);
        }
      }

      if (!row.hover) {
        this.patch(row, { hover: true });
      }
    },
    onSelectionChange(selected) {
      this.hasSelection = !!selected.length;
      if (!this.hasSelection) {
        this.removeHovered();
      }
    },
    onNoCellMouseEnter() {
      if (this.hasSelection) {
        // If the table is in a selection mode, donot unpatch
        // Because if we unpatch to hide action buttons selection will be lost on unpatch
        return;
      }

      this.removeHovered();
    },
    removeHovered() {
      const hovered = this.mutatedRows.filter((r) => r.hover);

      for (let i = 0; i < hovered.length; i += 1) {
        this.unpatch(hovered[i], ['hover']);
      }
    },
  },
};

export default countlyVue.components.BaseComponent.extend({
  name: 'ClyDatatable',
  components: {
    'cly-dropdown': ClyDropdown,
  },
  mixins: [
    countlyVue.mixins.commonFormatters,
    TableExtensionsMixin,
    MutationTrackerMixin,
    OverlayRowMixin,
  ],
  props: {
    hasExport: {
      type: Boolean,
      default: true,
      required: false,
    },
    keyFn: {
      type: Function,
      default(row) {
        return row._id;
      },
    },
    availablePageSizes: {
      type: Array,
      default() {
        return [5, 10, 20, 50, 100, 200, 1000];
      },
    },
    searchPlaceholder: {
      type: String,
      default: 'Search',
    },
    border: {
      type: Boolean,
      default: false,
    },
    hideTop: {
      type: Boolean,
      default: false,
    },
    forceLoading: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data() {
    return {
      slotMapping: {
        'header-left': 'header-left',
        'header-right': 'header-right',
        'footer-left': 'footer-left',
        'footer-right': 'footer-right',
        bottomline: 'bottomline',
      },
    };
  },
  computed: {
    isExportAvailable() {
      return this.dataSource?.export;
    },
    searchQueryProxy: {
      get() {
        if (this.displaySearch) {
          return this.controlParams.searchQuery;
        }
        return '';
      },
      set(query) {
        if (this.displaySearch) {
          _.extend(this.controlParams, { searchQuery: query, page: 1 });
        }
      },
    },
    forwardedSlots() {
      return Object.keys(this.$scopedSlots).reduce((slots, slotKey) => {
        if (!this.slotMapping[slotKey]) {
          /* eslint-disable no-param-reassign */
          slots[slotKey] = this.$scopedSlots[slotKey];
          /* eslint-enable no-param-reassign */
        }
        return slots;
      }, {});
    },
    isLoading() {
      if (this.forceLoading === true) {
        return true;
      }
      if (!this.dataSource) {
        return false;
      }
      return this.externalStatus !== 'ready' || (this.externalParams && !this.externalParams.ready);
    },
    classes() {
      const classes = [];
      if (!this.forceLoading && this.dataSource && this.externalStatus === 'silent-pending') {
        classes.push('silent-loading');
      }

      return classes;
    },
    sourceRows() {
      return this.dataView.rows;
    },
    commonScope() {
      return {
        diff: this.diff,
        patch: this.patch,
        unpatch: this.unpatch,
        dynamicCols: this.publicDynamicCols,
      };
    },
  },
  methods: {
    async onExportClick() {
      await this.dataSource.export();
    },
  },
});
</script>
<template>
  <div
    class="cly-vue-eldatatable"
    :class="classes"
  >
    <div
      v-loading="isLoading"
      element-loading-background="rgb(255,255,255,0.3)"
    >
      <div
        v-if="!hideTop"
        class="only:m-auto flex h-[56px] justify-between border-x-0 box-border p-3 border-t border-warm-gray-30 bg-white text-mm color-cool-gray-100"
        @mouseenter="onNoCellMouseEnter"
      >
        <div class="w-1/2 flex items-center">
          <slot
            v-bind="commonScope"
            name="header-left"
          />
        </div>
        <slot
          v-bind="commonScope"
          name="header-full"
        />
        <div class="flex w-1/2 justify-end">
          <slot
            v-bind="commonScope"
            name="header-right"
          />
          <div>
            <cly-select-x
              v-if="hasDynamicCols"
              v-model="controlParams.selectedDynamicCols"
              search-placeholder="Search in Columns"
              placeholder="Edit columns"
              title="Edit columns"
              mode="multi-check-sortable"
              placement="bottom-end"
              :auto-commit="false"
              :hide-default-tabs="true"
              :hide-all-options-tab="true"
              :options="availableDynamicCols"
            >
              <template #trigger>
                <el-button
                  size="small"
                  icon="ion-navicon-round"
                />
              </template>
            </cly-select-x>
          </div>
          <div
            v-if="hasExport && isExportAvailable"
            class="mx-1"
          >
            <el-button
              size="small"
              icon="cly-icon-btn cly-icon-download"
              @click="onExportClick"
            />
          </div>
          <div
            v-if="displaySearch"
            class="flex items-center"
          >
            <form>
              <el-input
                v-model="searchQueryProxy"
                autocomplete="off"
                size="small"
                class="cly-vue-eldatatable__search--grey w-[200px]"
                prefix-icon="ion-ios-search-strong"
                :placeholder="searchPlaceholder"
              />
            </form>
          </div>
        </div>
      </div>
      <el-table
        v-bind="$attrs"
        ref="elTable"
        :border="border"
        :row-key="keyFn"
        :data="mutatedRows"
        :span-method="tableSpanMethod"
        :default-sort="defaultSort"
        v-on="$listeners"
        @sort-change="onSortChange"
        @selection-change="onSelectionChange"
        @cell-mouse-enter="onCellMouseEnter"
      >
        <template
          v-if="!isLoading"
          slot="empty"
        >
          <cly-empty-datatable />
        </template>
        <template
          v-if="isLoading"
          slot="empty"
        >
          <div class="h-[200px]" />
        </template>
        <template
          v-for="(_, name) in forwardedSlots"
          #[name]
        >
          <slot
            :name="name"
            v-bind="commonScope"
          />
        </template>
      </el-table>
      <div
        class="flex justify-between h-[40px] border-x-0 box-border px-3 bg-white text-cool-gray-100"
        @mouseenter="onNoCellMouseEnter"
      >
        <div class="flex items-center">
          <div class="flex items-center justify-center grow-0 shrink-0 text-mm !mr-0">
            {{ $t("common.items-per-page") }}:
          </div>
          <div class="flex items-center text-mm color-cool-gray-100">
            <el-select
              v-model="controlParams.perPage"
              :borderless="true"
              style="margin-top: 2px;"
              :adaptive-length="true"
              size="mini"
            >
              <el-option
                v-for="pageSize in availablePageSizes"
                :key="pageSize"
                :value="pageSize"
                :label="pageSize"
              />
            </el-select>
          </div>
          <div
            class="bu-level-item"
            style="font-size: 11px"
          >
            {{ paginationInfo }}
          </div>
          <slot
            v-bind="commonScope"
            name="footer-left"
          />
        </div>
        <div class="flex justify-end items-center">
          <slot
            v-bind="commonScope"
            name="footer-right"
          />
          <div class="flex items-center text-mm color-cool-gray-100 !mr-0">
            <div>
              <el-select
                v-if="totalPages <= 200"
                v-model="controlParams.page"
                :borderless="true"
                :adaptive-length="true"
                size="mini"
              >
                <el-option
                  v-for="page in availablePages"
                  :key="page"
                  :value="page"
                  :label="page"
                />
              </el-select>
              <el-input
                v-else
                v-model="controlParams.page"
                type="number"
                :min="1"
                style="width: 160px"
                class="mr-1"
                :max="totalPages"
                size="mini"
              />
            </div>
          </div>
          <div class="flex items-center text-mm ml-1">
            of {{ formatNumber(totalPages) }} pages
          </div>
          <div :class="{'mx-1 flex items-center text-mm': true, 'cursor-pointer': true, 'color-cool-gray-100': prevAvailable, 'cool-gray-30': !prevAvailable}">
            <span
              :class="{disabled: !prevAvailable}"
              @click="goToFirstPage"
            ><i class="ion-ios-arrow-back" /><i class="ion-ios-arrow-back" /></span>
          </div>
          <div :class="{'mx-1 flex items-center text-mm': true, 'cursor-pointer': true, 'color-cool-gray-100': prevAvailable, 'cool-gray-30': !prevAvailable}">
            <span
              :class="{disabled: !prevAvailable}"
              @click="goToPrevPage"
            ><i class="ion-ios-arrow-back" /></span>
          </div>
          <div :class="{'mx-1 flex items-center text-mm': true, 'cursor-pointer': true, 'color-cool-gray-100': nextAvailable, 'cool-gray-30': !nextAvailable}">
            <span
              :class="{disabled: !nextAvailable}"
              @click="goToNextPage"
            ><i class="ion-ios-arrow-forward" /></span>
          </div>
          <div :class="{'mx-1 flex items-center text-mm': true, 'cursor-pointer': true, 'color-cool-gray-100': nextAvailable, 'cool-gray-30': !nextAvailable}">
            <span
              :class="{disabled: !nextAvailable}"
              @click="goToLastPage"
            ><i class="ion-ios-arrow-forward" /><i class="ion-ios-arrow-forward" /></span>
          </div>
        </div>
      </div>
      <div>
        <slot
          name="bottomline"
          v-bind="commonScope"
        />
      </div>
    </div>
  </div>
</template>
<style lang="scss">
    @use "../../styles/base/colors" as c;
    @use "../../styles/base/typography-variables" as t;
    @use "../../styles/base/variables" as v;
    @use "sass:map";

    .cly-vue-eldatatable {

        &.silent-loading {
            .el-loading-spinner {
                visibility: hidden;
            }
            .el-loading-mask {
                display: none!important;
            }
        }

        &__search {
            &--grey {
                input {
                    background-color: #FBFDFE;
                }
            }
        }

        .undo-row {
            position: absolute;
            left: 0;
            padding: 9px 0px;
            background-color: #FFD7D6;
            color: #2b2b2b;
            width: 100%;
            height: 100%;
            top: 0;
            z-index: 999;
            vertical-align: middle;
            display: flex;
            align-items: center;
            justify-content: center;
            a {
                color: #017AFF;
                cursor: pointer;
            }
        }

        .el-table-column--options .cly-vue-more-options {
            position: absolute;
            top: calc(50% - 16px);
            right: calc(50% - 20px);
        }

        div.cell {
            padding-left:16px;
            padding-right: 8px;
        }
    }
</style>
